import { useCallback, useContext, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { useDispatch, useSelector } from 'react-redux';

import { PullRequest } from 'src/components/types';
import {
  PR_APPROVE_INTERACTION,
  startMetric,
  useAbortMetricOnUnmount,
} from 'src/metrics';
import { UPDATE_PARTICIPANTS } from 'src/redux/pull-request/actions/constants';
import { updatePullRequest } from 'src/redux/pull-request/actions/update-pull-request';
import { getErrorMessageAsync } from 'src/redux/pull-request/sagas/utils/get-error-message';
import {
  getCurrentPullRequest,
  getIRAnalyticsTrackingAttributes,
} from 'src/redux/pull-request/selectors';
import urls from 'src/redux/pull-request/urls';
import { getPrReviewGroupsEnabled } from 'src/selectors/feature-selectors';
import {
  ExperienceName,
  useExperienceTracker,
} from 'src/utils/analytics/experience-tracker';
import authRequest from 'src/utils/fetch';

import { useReviewGroupsActions } from '../review-groups/provider';
import {
  DiffStateContext,
  SelectedDiffType,
} from '../tabs/diff/diff-state-context';

type HookCallbacks = {
  onStart?: () => void; // Called before request is fired off
  onSuccess?: () => void; // Called when request finishes successfully
  onError?: (error: Error) => void; // Called when an error occurs
  onFinished?: () => void; // Called when request finishes, regardless of success or failure
};

/** Sends a request to approve or unapprove the given pull request. Throws an
 * error on failure; returns without error on success. */
async function sendApprovalRequest(
  owner: string,
  slug: string,
  id: number,
  action: 'approve' | 'unapprove'
) {
  const url = urls.api.v20.approval(owner, slug, id);
  const authUrl = authRequest(url);
  const method = action === 'approve' ? 'POST' : 'DELETE';
  const response: Response = await fetch(authUrl, { method });

  if (!response.ok) {
    const message: string = await getErrorMessageAsync(response);
    const error = new Error(message);
    // @ts-ignore
    error.status = response.status;
    throw error;
  }
}

// This is an internal function to provide common behavior between the approval
// and unapproval hooks. The latter hooks can provide a `requests` callback to
// perform the actual mutation. Loading/error state and event callbacks are
// handled here.
function useApprovalMutation(
  requests: (owner: string, slug: string, id?: number) => Promise<void>,
  options?: HookCallbacks
) {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const callback = useCallback(
    async (owner: string, slug: string, id: number) => {
      try {
        if (options?.onStart) {
          options?.onStart();
        }
        setIsLoading(true);
        await requests(owner, slug, id);
        if (options?.onSuccess) {
          options?.onSuccess();
        }
        setIsError(false);
      } catch (e) {
        Sentry.captureException(e);
        if (options?.onError) {
          options?.onError(e);
        }
        setIsError(true);
      } finally {
        if (options?.onFinished) {
          options?.onFinished();
        }
        setIsLoading(false);
      }
    },
    [requests, options]
  );
  return { unapprovePullRequest: callback, isLoading, isError };
}

export function useApprovePullRequest(options?: HookCallbacks): {
  approvePullRequest: (
    owner: string,
    slug: string,
    id?: number
  ) => Promise<void>;
  isError: boolean;
  isLoading: boolean;
} {
  const updateParticipants = useUpdateParticipants();
  const {
    unapprovePullRequest: callback,
    isLoading,
    isError,
  } = useApprovalMutation(async (owner: string, slug: string, id?: number) => {
    if (id) {
      await sendApprovalRequest(owner, slug, id, 'approve');
      await updateParticipants(owner, slug, id);
    } else {
      throw new Error('Attempted to approve pull request with no ID available');
    }
  }, options);
  return { approvePullRequest: callback, isLoading, isError };
}

export function useUnapprovePullRequest(options?: HookCallbacks): {
  unapprovePullRequest: (
    owner: string,
    slug: string,
    id?: number
  ) => Promise<void>;
  isError: boolean;
  isLoading: boolean;
} {
  const updateParticipants = useUpdateParticipants();
  const {
    unapprovePullRequest: callback,
    isLoading,
    isError,
  } = useApprovalMutation(async (owner: string, slug: string, id?: number) => {
    if (id) {
      await sendApprovalRequest(owner, slug, id, 'unapprove');
      await updateParticipants(owner, slug, id);
    } else {
      throw new Error(
        'Attempted to unapprove pull request with no ID available'
      );
    }
  }, options);
  return { unapprovePullRequest: callback, isLoading, isError };
}

export function useApprovePullRequestWithMetrics(options?: HookCallbacks): {
  approvePullRequest: (
    owner: string,
    slug: string,
    id?: number | undefined
  ) => Promise<void>;
  isError: boolean;
  isLoading: boolean;
} {
  const {
    startExperience,
    succeedExperience,
    failExperience,
    abortExperience,
  } = useExperienceTracker(ExperienceName.PULL_REQUEST_APPROVE);
  useAbortMetricOnUnmount(PR_APPROVE_INTERACTION);

  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);
  const irTracking = useSelector(getIRAnalyticsTrackingAttributes);
  irTracking.irSelectedDiffType =
    SelectedDiffType[iterativeReviewSelectedDiff.selectedDiffType];

  return useApprovePullRequest({
    ...options,
    onStart: () => {
      startMetric(PR_APPROVE_INTERACTION);
      startExperience();
      if (options?.onStart) {
        options.onStart();
      }
    },
    onSuccess: () => {
      if (PR_APPROVE_INTERACTION.getData().state === 'started') {
        PR_APPROVE_INTERACTION.stop();
      }

      // IR tracking attributes for event: `ui taskSuccess pull-request/approve`
      succeedExperience({ irTracking });

      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (e: Error & { status?: number }) => {
      if (e.status && e.status >= 400 && e.status < 500) {
        abortExperience(`${e.status}`);
      } else {
        failExperience(
          e.message
            ? e
            : new Error(`PR approval experience failure ${e.status}`)
        );
      }
      if (PR_APPROVE_INTERACTION.getData().state === 'started') {
        PR_APPROVE_INTERACTION.cancel();
      }
      if (options?.onError) {
        options.onError(e);
      }
    },
  });
}

async function sendUpdateParticipantsRequest(
  owner: string,
  slug: string,
  id: number
) {
  const url = urls.api.v20.participants(owner, slug, id);
  const authUrl = authRequest(url);

  const response: Response = await fetch(authUrl);
  const json = await response.json();

  if (!response.ok) {
    throw Error(json.error.message);
  }
  return json;
}

export function useUpdateParticipants(options?: {
  onStart?: () => void;
  onError?: (error: Error) => void;
}): (owner: string, slug: string, id: number) => Promise<void> {
  const currentPullRequest: PullRequest | undefined = useSelector(
    getCurrentPullRequest
  );
  const dispatch = useDispatch();

  const isPrReviewGroupsEnabled = useSelector(getPrReviewGroupsEnabled);
  const [_, { fetchReviewGroups }] = useReviewGroupsActions();

  const callback = useCallback(
    async (owner: string, slug: string, id: number) => {
      try {
        if (id && currentPullRequest) {
          if (options?.onStart) {
            options?.onStart();
          }

          const json = await sendUpdateParticipantsRequest(owner, slug, id);

          // Refresh review groups status
          if (isPrReviewGroupsEnabled) {
            fetchReviewGroups(owner, slug, currentPullRequest.id.toString());
          }

          const updatedPullRequest = {
            ...currentPullRequest,
            participants: json.participants,
          };
          // We may not actually need UPDATE_PARTICIPANTS actions; generic PR
          // update action may be enough. No other UPDATE_PARTICIPANTS actions
          // besides SUCCESS are currently used.
          dispatch(
            updatePullRequest({
              type: UPDATE_PARTICIPANTS.SUCCESS,
              pullRequest: updatedPullRequest,
            })
          );
        } else {
          throw new Error(
            'Tried to update participants when missing ID or Pull Request'
          );
        }
      } catch (e) {
        dispatch({
          type: UPDATE_PARTICIPANTS.ERROR,
          payload: e.message,
        });
        if (options?.onError) {
          options?.onError(e);
        }
      }
    },
    [
      currentPullRequest,
      dispatch,
      options,
      fetchReviewGroups,
      isPrReviewGroupsEnabled,
    ]
  );
  return callback;
}
